/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <teleport to="body">
    <transition name="fade">
      <div
          v-if="showMenu" ref="menuContainer" class="context-menu-container shadow-lg" :style="positionStyle"
      >
        <ul class="list-unstyled mb-0">
          <li
              v-for="(option, index) in options"
              :key="index"
              class="context-menu-item"
              :class="{ 'disabled': !option.enabled, 'text-danger': option.class && option.class.includes('btn-danger') }"
              @click="handleOptionClick(option)"
          >
            <div class="icon-wrapper">
              <i v-if="option.icon" :class="['fas', 'fa-' + option.icon]"></i>
            </div>

            <span class="menu-text">{{ option.text }}</span>
          </li>
        </ul>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: "ContextMenu",
  props: {
    // Lista opcji zgodna z Twoim formatem
    options: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  emits: ["option-selected"],
  data() {
    return {
      isVisible: false,
      x: 0,
      y: 0,
      // Przechowujemy dane elementu, na którym kliknięto (np. wiersz tabeli)
      contextItem: null
    };
  },
  computed: {
    positionStyle() {
      return {
        top: `${this.y}px`,
        left: `${this.x}px`
      };
    },
    showMenu() { return this.isVisible; }
  },
  methods: {
    /**
     * Główna metoda otwierająca menu.
     * @param {MouseEvent} event - Zdarzenie kliknięcia (dla współrzędnych)
     * @param {Object} item - (Opcjonalne) Dane obiektu, którego dotyczy kliknięcie
     */
    show(payload) {
      const { event, item } = payload;
      event.preventDefault(); // Blokuje domyślne menu przeglądarki

      this.isVisible = true;
      this.contextItem = item;
      console.log("Starting context menu...",item);
      // Używamy nextTick, aby element zdążył się wyrenderować i miał wymiary
      this.$nextTick(() => {
        this.calculatePosition(event);
      });
    },

    close() {
      this.isVisible = false;
      // this.contextItem = null;
    },

    calculatePosition(event) {
      const menu = this.$refs.menuContainer;
      if (!menu) return;

      const { clientX: clickX, clientY: clickY } = event;
      const { innerWidth: windowWidth, innerHeight: windowHeight } = window;
      const { offsetWidth: menuWidth, offsetHeight: menuHeight } = menu;

      // Logika zapobiegająca wyjściu menu poza ekran
      // Horyzontalnie:
      if (clickX + menuWidth > windowWidth) {
        this.x = clickX - menuWidth;
      } else {
        this.x = clickX;
      }

      // Wertykalnie:
      if (clickY + menuHeight > windowHeight) {
        this.y = clickY - menuHeight;
      } else {
        this.y = clickY;
      }
    },

    handleOptionClick(option) {
      if (!option.enabled) return;

      // Emitujemy zdarzenie z akcją oraz danymi kontekstowymi (jeśli były przekazane)
      this.$emit("option-selected", {
        action: option.action,
        item: this.contextItem
      });

      this.close();
    },

    handleClickOutside(event) {
      // Jeśli menu jest otwarte i kliknięto poza nim -> zamknij
      // if (this.isVisible && this.$refs.menuContainer && !this.$refs.menuContainer.contains(event.target)) {
      //   this.close();
      // }
    }
  },
  mounted() {
    // Nasłuchiwanie kliknięć na całej stronie, aby zamknąć menu
    window.addEventListener("click", this.handleClickOutside);
    window.addEventListener("contextmenu", this.handleClickOutside); // Zamknij przy kliknięciu prawym gdzie indziej
    window.addEventListener("scroll", this.close); // Zamknij przy scrollowaniu
  },
  beforeUnmount() {
    window.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("contextmenu", this.handleClickOutside);
    window.removeEventListener("scroll", this.close);
  }
};
</script>

<style scoped>
.context-menu-container {
  position: fixed;
  z-index: 9999999; /* Bardzo wysoki indeks, aby być nad wszystkim */
  background-color: #ffffff;
  border: 1px solid #e1dfdd;
  border-radius: 4px;
  min-width: 180px;
  padding: 4px 0;
  font-family: "Segoe UI", sans-serif;
  color: #323130;
  overflow: hidden;
}

.context-menu-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.1s ease, color 0.1s ease;
  font-size: 13px;
  font-weight: 500;
  user-select: none;
}

.context-menu-item:hover {
  background-color: #f3f2f1;
  color: #0078D4; /* Twój kolor brandowy */
}

/* Stylizacja ikony */
.icon-wrapper {
  width: 24px;
  display: flex;
  justify-content: center;
  margin-right: 8px;
  color: #605e5c;
}

.context-menu-item:hover .icon-wrapper {
  color: #0078D4;
}

/* Opcja Danger (np. Usuń) */
.context-menu-item.text-danger:hover {
  background-color: #fdf2f2;
  color: #d13438 !important; /* Czerwony */
}
.context-menu-item.text-danger:hover .icon-wrapper {
  color: #d13438;
}

/* Stan Disabled */
.context-menu-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Animacja pojawiania się (Fade) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>